import { CHART_HEAD, CHART_UNDER_HEAD } from '@/components/charts/chart-header-layout'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'
import StatStripSkeleton from '@/components/ui/StatStripSkeleton'
import { cn } from '@/lib/utils'

/*
 * Market · Overview before its first response: the LOADED board, block for
 * block, in its order and at its heights — not V2's Loading frame
 * (`4236:96362`), whose 790px of blocks stood in for a ~2,000px page, so the
 * page grew by more than a screen the moment the data landed. The owner
 * approved departing from that frame.
 *
 * Cut for the pages revamp's board: headline, index panel with its one-row stat
 * strip, "Today's session", the movers pair, the Indices table (its first
 * eight) and Top brokers. Every block reuses the loaded component's own box
 * classes and line heights, so it matches at every width, not just a measured
 * one.
 *
 * The Indices table is a typical eight rows plus its "View more" line; mm-be's
 * count decides whether the loaded one has the line at all.
 *
 * Bars only, no words, so nothing here can be mistaken for, or found by a test
 * as, the loaded content. The page's own TitleRow is the exception, and
 * ScreenLayout draws it (see OverviewScreen).
 */

/** A bar vertically centred in a text line box of `line` height. */
function Line({ line, bar }: { line: string; bar: string }) {
  return (
    <div className={cn('flex items-center', line)}>
      <Skeleton className={bar} />
    </div>
  )
}

/**
 * OverviewIndexPanel: the band's head (`ChartHeader`, its own grid by name) — the headline's 28px
 * line over the 40px figure row, beside the Line/Candles track and the 40px chart button over the
 * six 48px boxed periods (38 tall with their hairlines; `h-9` here) — then the 212px chart and the
 * five stats.
 */
function IndexPanelSkeleton() {
  return (
    <div className="flex min-w-0 flex-col">
      <div className={CHART_HEAD.root}>
        <div className={CHART_HEAD.grid}>
          <div className={CHART_HEAD.info}>
            <Line line="h-7" bar="h-5 w-64" />
            <Line line="h-10" bar="h-8 w-56" />
          </div>
          <div className={CHART_HEAD.actions}>
            <div className={cn(CHART_HEAD.controls, 'justify-between')}>
              <Skeleton className="h-10 w-36" />
              <Skeleton className="size-10" />
            </div>
            <Skeleton className="h-9 w-74" />
          </div>
        </div>
      </div>
      <Skeleton className={cn(CHART_UNDER_HEAD, 'h-53 w-full')} />
      <StatStripSkeleton cells={5} surface="ruled" className="mt-6" />
    </div>
  )
}

/** BreadthBar: flat — its head row, the three counts, the 8px bar and the hairline 20 under it. */
function BreadthSkeleton() {
  return (
    <div className="flex flex-col">
      <div className="flex min-h-9 items-center justify-between gap-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-16" />
      </div>
      <div className="mt-3 flex h-7 items-center justify-between gap-4">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-24" />
      </div>
      <Skeleton className="mt-2 h-2 w-full" />
      <div className="mt-5 h-px" />
    </div>
  )
}

/** The brief card: Mitra's 120px column beside the bubble, which stands 16 off the baseline. */
function BriefCardSkeleton() {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-end gap-4">
      <Skeleton className="h-20 w-14 @md:h-30 @md:w-21" />
      <Skeleton className="mb-4 h-28 w-full" />
    </div>
  )
}

/** SectionHead (`card` size) above a table: its 36px row, a title and a link. */
function SectionHeadSkeleton({ title }: { title: string }) {
  return (
    <div className="flex min-h-9 items-center justify-between gap-3">
      <Skeleton className={cn('h-4', title)} />
      <Skeleton className="h-4 w-16" />
    </div>
  )
}

/** One half of the movers pair: the head, then five flat 60px rows (logo, ticker stack, figures). */
function MoversSkeleton({ title }: { title: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <SectionHeadSkeleton title={title} />
      <div className="flex flex-col">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="flex h-15 items-center gap-3">
            <Skeleton className="size-9 shrink-0 rounded-full" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="ml-auto h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  )
}

/** The Indices table's folded preview: its first eight rows. */
const INDEX_ROWS = 8

export function MarketBoardSkeleton() {
  return (
    <>
      <IndexPanelSkeleton />
      <BriefCardSkeleton />
      <BreadthSkeleton />
      <div className="grid gap-6 @2xl:grid-cols-2">
        <MoversSkeleton title="w-28" />
        <MoversSkeleton title="w-24" />
      </div>
      <div className="flex flex-col gap-3">
        <SectionHeadSkeleton title="w-20" />
        <DataTableSkeleton rows={INDEX_ROWS} columns={3} />
      </div>
      <div className="flex flex-col gap-3">
        <SectionHeadSkeleton title="w-40" />
        <DataTableSkeleton rows={7} columns={3} rowHeight="lg" />
      </div>
    </>
  )
}
